import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from './api/http';
import { useAuth } from './auth/AuthProvider';
import { resolveActiveCustomerContext } from './auth/customerContext';
import OperatorDashboardPage from './operatorDashboard/OperatorDashboardPage';
import {
  isActiveForklift,
  isOperatorUseBlocked,
  matchesForkliftSearch,
} from './operatorDashboard/operatorDashboardUtils';

export default function OperatorDashboard() {
  const navigate = useNavigate();
  const { session } = useAuth();

  const [forklifts, setForklifts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hasMultipleCustomers, setHasMultipleCustomers] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [search, setSearch] = useState('');

  const userId = session?.userId ?? null;

  const readSelectedCustomerId = () => {
    try {
      const v = Number(localStorage.getItem('selectedCustomerId') || 0);
      return v > 0 ? v : null;
    } catch {
      return null;
    }
  };

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/api/forklifts');
      setForklifts(Array.isArray(data) ? data : []);
    } catch {
      setForklifts([]);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // If operator is linked to >1 customers and no valid active selection exists, force selection screen.
  useEffect(() => {
    if (!userId) return undefined;

    let cancelled = false;

    const run = async () => {
      try {
        const { data } = await api.get(`/api/users/${userId}/customers`, { skipCustomer: true });
        if (cancelled) return;

        const rows = Array.isArray(data) ? data : [];
        const ids = rows
          .map((r) => Number(r.customer_id ?? r.id))
          .filter((n) => Number.isFinite(n) && n > 0);

        const count = ids.length;
        setHasMultipleCustomers(count > 1);

        if (count > 1) {
          const selectedCid = readSelectedCustomerId();
          const sessionCid = session?.customerId != null ? Number(session.customerId) : null;
          const activeCid = selectedCid || sessionCid || null;

          const activeIsValid = activeCid != null && ids.includes(Number(activeCid));

          if (!activeIsValid) {
            try {
              if (sessionCid != null && ids.includes(sessionCid)) {
                localStorage.setItem('pendingCustomerId', String(sessionCid));
              } else {
                localStorage.removeItem('pendingCustomerId');
              }
            } catch {}

            navigate('/select-account', { replace: true });
          }
        }
      } catch {
        if (cancelled) return;
        setHasMultipleCustomers(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [userId, session?.customerId, navigate]);

  const startChecklist = (forklift) => {
    if (busy || isOperatorUseBlocked(forklift)) return;
    setBusy(true);
    const unitId = forklift?.unitId ? String(forklift.unitId) : '';
    navigate(`/operator/checklist?unitId=${encodeURIComponent(unitId)}`);
  };

  const startSafetyReport = (forklift) => {
    if (busy) return;
    setBusy(true);
    const unitId = forklift?.unitId ? String(forklift.unitId) : '';
    navigate(`/operator/checklist?unitId=${encodeURIComponent(unitId)}&safetyReport=1`);
  };

  const filteredForklifts = useMemo(
    () => forklifts.filter(isActiveForklift).filter((f) => matchesForkliftSearch(f, search)),
    [forklifts, search]
  );

  const title = resolveActiveCustomerContext(session).customerName;

  return (
    <OperatorDashboardPage
      busy={busy}
      filteredForklifts={filteredForklifts}
      hasMultipleCustomers={hasMultipleCustomers}
      onRefresh={load}
      onSafetyReport={startSafetyReport}
      onSelectForklift={setSelectedRowId}
      onStartChecklist={startChecklist}
      search={search}
      selectedRowId={selectedRowId}
      setSearch={setSearch}
      title={title}
    />
  );
}
